import Util from '../../util.js';

class Select extends HTMLElement {

    #selectElement = null;
    #changeHandler = null;
    #inputHandler = null;

    #template = document.createElement('template');
    $templateMarkup = `
        <select>
            <slot></slot>
        </select>
        <span class="dropdown-icon">
            <i class="material-symbols-rounded">arrow_drop_down</i>
        </span>
    `;

    constructor() {
        super();
        this.attachShadow({mode: 'open'});
        this.addStyle = Util.addStyle;
        this.addStyle('/src/styles/neptune.css');
        this.addStyle('/src/components/form/select/select.css');
        this.#template.innerHTML = this.$templateMarkup;
        this.shadowRoot.appendChild(this.#template.content.cloneNode(true));
    }

    connectedCallback() {
        this.setSelect();
        this.#setupEventListeners();
    }

    disconnectedCallback() {
        this.#removeEventListeners();
    }

    static get observedAttributes() {
        return ['error', 'disabled', 'placeholder'];
    }

    #validateOptions() {
        const options = Array.from(this.querySelectorAll('option'));
        
        if (options.length === 0) {
            const errorMessage = 'Invalid <neptune-select>: Select component must contain at least one <option> element.';
            throw new Error(errorMessage);
        }

        const hasValidOption = options.some(option => {
            const value = (option.getAttribute('value') || '').trim();
            const text = (option.textContent || '').trim();
            return value !== '' && text !== '';
        });

        if (!hasValidOption) {
            const errorMessage = 'Invalid <neptune-select>: Select component must contain at least one valid <option> with both value attribute and text content.';
            console.error(errorMessage);
            throw new Error(errorMessage);
        }
    }

    #setupEventListeners() {
        this.#selectElement = this.shadowRoot.querySelector('select');
        
        this.#changeHandler = () => {
            this.dispatchEvent(new Event('change', {
                bubbles: true,
                composed: true
            }));
        };
        
        this.#inputHandler = () => {
            this.dispatchEvent(new Event('input', {
                bubbles: true,
                composed: true
            }));
        };
        
        this.#selectElement.addEventListener('change', this.#changeHandler);
        this.#selectElement.addEventListener('input', this.#inputHandler);
    }

    #removeEventListeners() {
        if (this.#selectElement && this.#changeHandler && this.#inputHandler) {
            this.#selectElement.removeEventListener('change', this.#changeHandler);
            this.#selectElement.removeEventListener('input', this.#inputHandler);
            
            this.#selectElement = null;
            this.#changeHandler = null;
            this.#inputHandler = null;
        }
    }

    setSelect() {
        this.#validateOptions();
        
        const selectElement = this.shadowRoot.querySelector('select');
        
        selectElement.innerHTML = '';
        
        const placeholder = this.getAttribute('placeholder');
        if (placeholder) {
            const placeholderOption = document.createElement('option');
            placeholderOption.value = '';
            placeholderOption.textContent = placeholder;
            placeholderOption.disabled = true;
            placeholderOption.selected = true;
            selectElement.appendChild(placeholderOption);
        }
        
        this.#populateOptions(selectElement);

        selectElement.disabled = this.hasAttribute('disabled');
    }

    #populateOptions(selectElement) {
        const options = Array.from(this.querySelectorAll('option'));
        
        let hasSelectedOption = false;
        let selectedValue = null;
        
        options.forEach(option => {
            const clonedOption = option.cloneNode(true);
            selectElement.appendChild(clonedOption);
            
            if (option.selected && !option.disabled) {
                hasSelectedOption = true;
                selectedValue = option.value;
            }
        });
        
        if (hasSelectedOption && selectedValue !== null) {
            selectElement.value = selectedValue;
        }
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'disabled') {
            const selectElement = this.shadowRoot.querySelector('select');
            if (selectElement) {
                selectElement.disabled = this.hasAttribute('disabled');

                if (selectElement.disabled) {
                    selectElement.setAttribute('aria-disabled', 'true');
                } else {
                    selectElement.removeAttribute('aria-disabled');
                }
            }
            return;
        }
        if (name === 'error') {
            const selectElement = this.shadowRoot.querySelector('select');
            if (selectElement) {
                if (this.hasAttribute('error')) {
                    selectElement.setAttribute('aria-invalid', 'true');
                } else {
                    selectElement.removeAttribute('aria-invalid');
                }
            }
            return;
        }
        if (name === 'placeholder') {
            if (oldValue !== newValue && this.isConnected) {
                this.setSelect();
            }
            return;
        }
        if (oldValue !== newValue && this.isConnected) {
            this.setSelect();
        }
    }

    get value() {
        const selectElement = this.shadowRoot.querySelector('select');
        return selectElement ? selectElement.value : '';
    }

    set value(val) {
        const selectElement = this.shadowRoot.querySelector('select');
        if (selectElement) {
            selectElement.value = val;
        }
    }
}

customElements.define('neptune-select', Select);

export default Select;